<template>
  <div :class="['eg-table-base', tableClass]" :data-testid="dataTestid">
    <!-- Header slot (for actions like "Add Submissions", bulk actions, etc.) -->
    <slot
      name="header"
      :selected-ids="internalSelectedIds"
      :selected-all="selectedAll"
    >
      <!-- Default header can be placed here if needed -->
    </slot>

    <!-- Filters slot -->
    <slot
      name="filters"
      :filters="filters"
      :is-filtered="isFiltered"
      :on-filter-change="handleFilterChange"
      :selected-ids="internalSelectedIds"
      :selected-all="selectedAll"
      :total="total"
      :limit="limit"
    >
      <!-- Default filters can be placed here if needed -->
    </slot>

    <!-- Table component -->
    <eg-selectable-table
      v-model="internalSelectedIds"
      :class="['eg-table', tableWrapperClass]"
      :selectable="selectable"
      :split-select="splitSelect"
      :items="items"
      :fields="fields"
      :total="total"
      :limit="limit"
      :current-page="currentPage"
      :get-disabled="getDisabled"
      :loading="tableLoading"
      :initial-loading="initialLoading"
      :click-action="handleRowClick"
      :show-pagination="showPagination"
      data-testid="eg-selectable-table"
      @onChangePage="handlePageChange"
    >
      <!-- Empty state slot -->
      <template #empty-state>
        <slot
          name="empty-state"
          :is-filtered="isFiltered"
          :table-loading="tableLoading"
          :items-count="items.length"
        >
          <!-- Default empty state fallback (for backward compatibility) -->
          <empty-state
            v-if="!tableLoading && isFiltered && !items.length"
            key="filtered"
            :title="emptyFilteredTitle"
            :description="emptyFilteredDescription"
            class="m-auto"
            data-testid="empty-state-filtered"
          />
          <empty-state
            v-if="!tableLoading && !isFiltered && !items.length"
            key="empty"
            :title="emptyTitle"
            class="m-auto"
            data-testid="empty-state"
          >
            <template #descriptionSlot>
              <slot name="empty-description">
                {{ emptyDescription }}
              </slot>
            </template>
          </empty-state>
        </slot>
      </template>

      <!-- Head slots -->
      <template #head()="{ label, field }">
        <slot name="head" :label="label" :field="field" :on-sort="handleSort">
          <div
            class="d-flex align-items-center cursor-pointer"
            @click.prevent="handleSort(field)"
          >
            {{ label }}
            <eg-icon
              v-if="field.egSortable"
              :style="{ color: sortBy === field.key ? '#343A40' : 'unset' }"
              :icon="
                sortBy === field.key
                  ? `arrow-${sortDesc ? 'down' : 'up'}`
                  : 'arrow-down'
              "
              data-testid="sort-icon"
            />
          </div>
        </slot>
      </template>

      <template #head(actions)="{ label }">
        <slot name="head-actions" :label="label">
          {{ label }}
        </slot>
      </template>

      <!-- Table colgroup for column sizing -->
      <template #table-colgroup="{ fields: tableFields }">
        <slot name="table-colgroup" :fields="tableFields">
          <col
            v-for="field in tableFields"
            :key="field.key"
            :class="field.columnClass"
            data-testid="table-colgroup"
          />
        </slot>
      </template>

      <!-- Pass through all cell slots -->
      <template
        v-for="field in fields"
        #[`cell(${field.key})`]="{ item, value }"
      >
        <slot :name="`cell(${field.key})`" :item="item" :value="value">
          <span
            :key="`${field.key}-${item?.id || ''}`"
            :class="field.key === 'name' ? 'paragraph-s' : 'caption-s'"
            >{{ value }}</span
          >
        </slot>
      </template>

      <!-- Actions cell -->
      <template #cell(actions)="{ item }">
        <slot name="cell-actions" :item="item">
          <div class="d-flex align-items-center justify-content-end">
            <!-- Default actions can be customized via slot -->
          </div>
        </slot>
      </template>
    </eg-selectable-table>

    <!-- Additional content slot (for modals, etc.) -->
    <slot name="additional-content" />
  </div>
</template>

<script>
import EgSelectableTable from "@/components/global/eg_selectable_table";
import EmptyState from "@/components/global/empty_state.vue";

export default {
  name: "EgTableBase",

  components: {
    EgSelectableTable,
    EmptyState,
  },

  props: {
    // Table data
    items: {
      type: Array,
      required: true,
    },
    fields: {
      type: Array,
      required: true,
    },
    total: {
      type: Number,
      required: true,
    },
    limit: {
      type: Number,
      default: 20,
    },
    currentPage: {
      type: Number,
      default: 1,
    },

    // Selection
    selectedAll: {
      type: Boolean,
      default: false,
    },
    selectable: {
      type: Boolean,
      default: false,
    },
    splitSelect: {
      type: Boolean,
      default: true,
    },
    getDisabled: {
      type: Function,
      default: () => () => false,
    },

    // Sorting
    sortBy: {
      type: String,
      default: "",
    },
    sortDesc: {
      type: Boolean,
      default: false,
    },

    // Loading states
    tableLoading: {
      type: Boolean,
      default: false,
    },
    initialLoading: {
      type: Boolean,
      default: false,
    },

    // Filters
    filters: {
      type: Object,
      default: () => ({}),
    },
    isFiltered: {
      type: Boolean,
      default: false,
    },

    // Empty states
    emptyTitle: {
      type: String,
      default: "No items yet",
    },
    emptyDescription: {
      type: String,
      default: "Start by creating your first item.",
    },
    emptyFilteredTitle: {
      type: String,
      default: "No items found.",
    },
    emptyFilteredDescription: {
      type: String,
      default: "Try again or clear all filters to view results.",
    },

    // Click action
    clickAction: {
      type: Function,
      default: null,
    },

    // Styling
    tableClass: {
      type: String,
      default: "",
    },
    tableWrapperClass: {
      type: String,
      default: "w-100",
    },

    // Testing
    dataTestid: {
      type: String,
      default: "eg-table-base",
    },

    // Pagination
    showPagination: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      internalSelectedIds: [],
    };
  },

  watch: {
    "internalSelectedIds.length"() {
      this.$emit("selectedAll", false);
    },

    // Reset selection when pagination changes
    total() {
      this.internalSelectedIds = [];
    },
    limit() {
      this.internalSelectedIds = [];
    },
    currentPage() {
      this.internalSelectedIds = [];
    },
  },

  methods: {
    handleSort(field) {
      if (!field.egSortable) return;

      const sortDesc = field.key === this.sortBy ? !this.sortDesc : false;

      this.$emit("getData", {
        sortBy: field.key,
        sortDesc: sortDesc,
      });
    },

    handleFilterChange(params) {
      this.$emit("getData", params);
    },

    handlePageChange(page) {
      this.$emit("onChangePage", page);
    },

    handleRowClick(item) {
      if (this.clickAction) {
        this.clickAction(item);
      } else {
        this.$emit("row-click", item);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.eg-table-base {
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: var(--color-surface-neutral-background, #fff);

  &::v-deep {
    --table-height: 100%;
    --min-table-height: 350px;
    --table-header-height: 48px;
    --max-row-height: calc(
      var(--table-height) - var(--table-header-height) - 2px
    );

    .b-table-sticky-header {
      max-height: 100%;
      min-height: var(--min-table-height);
      height: var(--table-height);
      margin-bottom: 0;
      overflow: unset;
    }

    .b-table-bottom-row > td {
      height: var(--max-row-height);
    }

    .selectable-table-wrapper {
      height: var(--table-height);
      display: flex;
    }

    .selectable-table {
      width: 100%;
      display: flex;
      flex-direction: column;
      margin-top: 0 !important;
      border-top: none;
      border-radius: 0.25rem;
      border-top-left-radius: 0;
      border-top-right-radius: 0;
      height: var(--table-height);
      min-height: var(--min-table-height);
      @media (max-width: 768px) {
        border-top: 1px solid hsl(0deg, 0%, 87%);
        border-top-left-radius: 0.25rem;
        border-top-right-radius: 0.25rem;
      }

      &:not(:has(.pagination)) {
        & .table tr.b-table-bottom-row {
          border-top: 1px solid #dee2e6;
          border-bottom: none;
          height: 48px;
        }
      }

      & .table tr td {
        padding-top: 16px;
        padding-bottom: 16px;
        vertical-align: middle;
      }

      & .table tr.b-table-top-row td {
        padding: 0;
      }

      & .table tr th {
        vertical-align: middle;
        white-space: normal;
        background: var(--color-surface-neutral-container-1, #f6f8fa);
        color: var(--color-text-neutral-secondary, #566370);
        font-family: var(--typography-font-family-text-primary, Inter);
        font-size: var(--typography-font-size-fs-200);
        font-style: normal;
        font-weight: var(--typography-font-weight-fw-600, 600);
        line-height: 120%;
      }

      .custom-control.custom-checkbox {
        .custom-control-input {
          height: 3rem;
          width: 2.5rem;
          top: -20%;
          left: -43%;
          cursor: pointer;
        }
        .custom-control-label {
          cursor: pointer;
        }
        input:hover + .custom-control-label:before {
          border-color: var(--purple-600);
        }
        input:focus + .custom-control-label:before {
          box-shadow: 0 0 0 0.2rem var(--purple-100);
        }
      }
    }
    .eg-filters-selector-dropdown {
      .btn:focus {
        text-transform: none;
      }
    }
    .empty-state {
      height: calc(100vh - 364px - var(--maintenance-height));
    }
  }
}

.cursor-pointer {
  cursor: pointer;
}

// Column sizing classes
.col-select {
  width: 48px;
}

.col-name {
  max-width: 25%;
}

.col-grade-level,
.col-intensity,
.col-language {
  width: 120px;
}

.col-created-by {
  width: 150px;
}

.col-created-date,
.col-grade-date {
  width: 150px;
}

.col-actions {
  width: 100px;
}

.col-grade,
.col-status {
  width: 135px;
}

.col-flags {
  width: 38px;

  @media (min-width: 992px) {
    width: 70px;
  }
}

// Common filter styles for both essays and rubrics tables
.eg-table-base::v-deep {
  // TODO: after using the template, replace correct classes
  .eg-table-base-filters {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    align-items: flex-end;
    border-bottom: 1px solid var(--color-stroke-neutral-default);

    .desktop,
    &.desktop {
      @media (max-width: 768px) {
        display: none;
      }
    }

    .tablet,
    &.tablet {
      display: none;

      @media (max-width: 768px) {
        display: flex;
      }
    }

    @media (max-width: 768px) {
      gap: 8px;
      margin-bottom: 1rem;
      border-bottom-left-radius: 0.25rem;
      border-bottom-right-radius: 0.25rem;
    }

    & > .form-group {
      flex: 0 1 100%;
      max-width: 100%;

      @media (min-width: 768px) {
        flex: 0 1 215px;
        max-width: 215px;
      }
    }

    .btn-link:hover {
      text-decoration: unset;
    }

    .search-input-wrapper .form-control {
      border-color: var(--color-surface-neutral-container-5);
    }
  }

  .selected-text {
    border: 1px solid transparent;
  }

  .clear-all {
    color: #6c757d;
    flex: 0 1 75px;
    font-size: 14px;
    font-weight: 400;
    line-height: 17px;
    flex-shrink: 0;
  }

  .filters {
    gap: 8px;

    @media (max-width: 768px) {
      flex-wrap: wrap;
    }
  }

  .text-info:focus {
    box-shadow: none;
    text-decoration: none;
  }

  .divider {
    margin: 0 8px;
    height: 31px;
    border-left: 1px solid var(--secondary);
  }
}
</style>
